import { useSetAtom } from 'jotai';
import moment from 'moment';
import { useApolloClient, useMutation } from '@apollo/client';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { EditorActions } from '../editor/editorSlice';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess, TopicPrivacy } from '../graphql/generated/graphql';
import { postToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import { isAddOrShareMenuOpenAtom } from './controlBar/controlBarMenuAtoms';

const CACHED_POST_QUERY = gql(/** @complexity 3 */ `
  query UsePublishPostCachedPost($id: ID!) {
    post(id: $id) {
      id
      linkAccess
      topics {
        id
      }
    }
  }
`);

const PUBLISH_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation updatePost($id: ID!, $published: Boolean!) {
    post: updatePost(id: $id, published: $published) {
      id
      publishedAt
      linkAccess
      privacy
    }
  }
`);

const usePublishPost = (id: string) => {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const [updatePost] = useMutation(PUBLISH_POST_MUTATION);
  const setIsAddOrShareMenuOpen = useSetAtom(isAddOrShareMenuOpenAtom);

  const publishPost = async (published: boolean) => {
    const cachedPostResult = client.readQuery({
      query: CACHED_POST_QUERY,
      variables: { id },
    });

    const { data } = await updatePost({
      variables: { id, published },
      optimisticResponse: {
        __typename: 'RootMutationType',
        post: {
          __typename: 'Post',
          id,
          linkAccess:
            // The server turns disabled linkAccess into internal, so this is reflected here
            // But if it's public, we want to keep it public to prevent flickering public icons
            cachedPostResult?.post.linkAccess === PostLinkAccess.Disabled
              ? PostLinkAccess.Internal
              : (cachedPostResult?.post.linkAccess ?? PostLinkAccess.Internal),
          privacy: TopicPrivacy.Open,
          publishedAt: published ? moment().toISOString() : null,
        },
      },
    });

    const hasTopic =
      cachedPostResult && cachedPostResult.post.topics.length > 0;

    if (data?.post) {
      dispatch(EditorActions.readOnly(published));
    }

    if (published && !hasTopic) {
      dispatch(
        UIActions.pushToast(
          postToasts.postPublished({
            onAddTopics: () => {
              dispatch(EditorActions.readOnly(false));
              setTimeout(() => {
                setIsAddOrShareMenuOpen(true);
              }, 1);
            },
          }),
          { duration: 7000 },
        ),
      );
    }
  };

  return publishPost;
};

export default usePublishPost;
